import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';

export const VOICE_INPUT_STATE = {
  IDLE: 'idle',
  LISTENING: 'listening',
  ERROR: 'error',
} as const;

export type VoiceInputState = typeof VOICE_INPUT_STATE[keyof typeof VOICE_INPUT_STATE];

const STT_RESULT_EVENT = 'SPEECH_TO_TEXT_RESULT';
const STT_ERROR_EVENT = 'SPEECH_TO_TEXT_ERROR';

const LOCALE_MAP: { [k: string]: string } = {
  en: 'en-IN',
  hi: 'hi-IN',
  kn: 'kn-IN',
  te: 'te-IN',
  mr: 'mr-IN',
  bn: 'bn-IN',
  ml: 'ml-IN',
  ta: 'ta-IN',
};

const FALLBACK_LOCALE = 'en-IN';

@Injectable({ providedIn: 'root' })
export class VoiceInputService {
  private readonly appBridge = inject(AppWebBridgeService);
  private readonly broadcast = inject(BroadcastService);
  private readonly conn = inject(ConnectionService);
  private readonly eventLogger = inject(EventLoggerService);

  private readonly stateSubject = new BehaviorSubject<VoiceInputState>(VOICE_INPUT_STATE.IDLE);
  readonly state$: Observable<VoiceInputState> = this.stateSubject.asObservable();

  isAvailable(): boolean {
    return this.appBridge.isAppWebBridgeLoaded();
  }

  get state(): VoiceInputState {
    return this.stateSubject.value;
  }

  reset(): void {
    if (this.stateSubject.value !== VOICE_INPUT_STATE.IDLE) {
      this.stateSubject.next(VOICE_INPUT_STATE.IDLE);
    }
  }

  listen(): Observable<string> {
    return new Observable<string>((subscriber): (() => void) => {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'chat_voice_icon' }));

      const locale = this.resolveLocale();
      const cancel$ = new Subject<void>();
      let bridgeLaunched = false;

      this.appBridge.requestSpeechToText(locale).then((launched: boolean): void => {
        if (!launched) {
          this.eventLogger.cleverTapEvent(
            'impression',
            JSON.stringify({ name: 'voice_input_error', reason: 'bridge_unavailable' }),
          );
          this.stateSubject.next(VOICE_INPUT_STATE.ERROR);
          subscriber.error(new Error('voice_bridge_unavailable'));
          return;
        }
        bridgeLaunched = true;
        this.stateSubject.next(VOICE_INPUT_STATE.LISTENING);
        this.eventLogger.cleverTapEvent(
          'impression',
          JSON.stringify({ name: 'voice_input_started', locale }),
        );

        this.broadcast.on<any>(STT_RESULT_EVENT)
          .pipe(takeUntil(cancel$), take(1))
          .subscribe((data: any): void => {
            const text = this.parseResultText(data);
            this.stateSubject.next(VOICE_INPUT_STATE.IDLE);
            if (text) {
              this.eventLogger.cleverTapEvent(
                'impression',
                JSON.stringify({ name: 'voice_input_received', input_string: text }),
              );
              subscriber.next(text);
              subscriber.complete();
            } else {
              this.eventLogger.cleverTapEvent(
                'impression',
                JSON.stringify({ name: 'voice_input_empty' }),
              );
              subscriber.complete();
            }
          });

        this.broadcast.on<any>(STT_ERROR_EVENT)
          .pipe(takeUntil(cancel$), take(1))
          .subscribe((data: any): void => {
            const reason = this.parseErrorReason(data);
            this.stateSubject.next(VOICE_INPUT_STATE.ERROR);
            this.eventLogger.cleverTapEvent(
              'impression',
              JSON.stringify({ name: 'voice_input_error', reason }),
            );
            subscriber.error(new Error(reason));
          });
      });

      return (): void => {
        cancel$.next();
        cancel$.complete();
        if (bridgeLaunched && this.stateSubject.value === VOICE_INPUT_STATE.LISTENING) {
          this.stateSubject.next(VOICE_INPUT_STATE.IDLE);
        }
      };
    });
  }

  notifyMessageSentWithVoice(text: string): void {
    if (!text) return;
    this.eventLogger.cleverTapEvent(
      'click',
      JSON.stringify({ name: 'voice_input_sent', input_string: text }),
    );
  }

  notifyTooltipShown(): void {
    this.eventLogger.cleverTapEvent(
      'impression',
      JSON.stringify({ name: 'voice_tooltip_shown' }),
    );
  }

  private resolveLocale(): string {
    const lang = this.conn.getActingUser()?.get('languagePreference');
    if (lang && LOCALE_MAP[lang]) return LOCALE_MAP[lang];
    return FALLBACK_LOCALE;
  }

  private parseResultText(data: any): string {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data.replace(/\\/g, '')) : data;
      return (parsed?.text || '').trim();
    } catch {
      return '';
    }
  }

  private parseErrorReason(data: any): string {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data.replace(/\\/g, '')) : data;
      return parsed?.reason || 'unknown';
    } catch {
      return 'unknown';
    }
  }
}
